// GigShip Platform - Frontend Constants
// Constants used across the React frontend application

// API Configuration
// IMPORTANT: Must point to the BACKEND (api.gigship.com), NOT the frontend (www.gigship.com).
// If VITE_API_URL is not set, the production fallback is used.
const _rawApiBaseUrl = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? 'https://api.gigship.com/api' : 'http://localhost:5000/api');
const _normalizedApiBaseUrl = _rawApiBaseUrl.replace(/\/+$/, '');
// Guarantee exactly one trailing /api segment, regardless of how VITE_API_URL
// is set in the deployment environment — mirrors the normalization
// AuthContext.jsx already applies to its axios baseURL, so raw fetch()
// callers using this constant get the same correct URL axios-based callers
// already get. Root cause of a missing-/api 404 on GET /gigs/user/:id and
// other raw-fetch call sites.
export const API_BASE_URL = _normalizedApiBaseUrl.endsWith('/api') ? _normalizedApiBaseUrl : `${_normalizedApiBaseUrl}/api`;
export const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'https://api.gigship.com';

// User Roles
export const USER_ROLES = {
  GIGGER: 'gigger',
  SHIPPER: 'shipper',
  HYBRID: 'hybrid',
  ADMIN: 'admin',
  MEDIATOR: 'mediator'
};

// Order Status
export const ORDER_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  IN_PROGRESS: 'in_progress',
  DELIVERED: 'delivered',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  DISPUTED: 'disputed',
  REFUNDED: 'refunded'
};

// Order Status Display Names
export const ORDER_STATUS_LABELS = {
  pending: 'Pending',
  accepted: 'Accepted',
  in_progress: 'In Progress',
  delivered: 'Delivered',
  completed: 'Completed',
  cancelled: 'Cancelled',
  disputed: 'Disputed',
  refunded: 'Refunded'
};

// Order Status Colors (Tailwind CSS classes)
export const ORDER_STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  accepted: 'bg-blue-100 text-blue-800 border-blue-200',
  in_progress: 'bg-purple-100 text-purple-800 border-purple-200',
  delivered: 'bg-orange-100 text-orange-800 border-orange-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  cancelled: 'bg-gray-100 text-gray-800 border-gray-200',
  disputed: 'bg-red-100 text-red-800 border-red-200',
  refunded: 'bg-pink-100 text-pink-800 border-pink-200'
};

// Payment Status
export const PAYMENT_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  FAILED: 'failed',
  REFUNDED: 'refunded',
  DISPUTED: 'disputed'
};

// Gig Status
export const GIG_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  DEACTIVATED: 'deactivated'
};

// Location Types
export const LOCATION_TYPES = {
  REMOTE: 'remote',
  LOCAL: 'local',
  HYBRID: 'hybrid'
};

// Location Type Labels
export const LOCATION_TYPE_LABELS = {
  remote: 'Remote Work',
  local: 'On-site Work',
  hybrid: 'Flexible (Remote/On-site)'
};

// Package Types
export const PACKAGE_TYPES = {
  BASIC: 'basic',
  STANDARD: 'standard',
  PREMIUM: 'premium'
};

// Package Type Labels
export const PACKAGE_TYPE_LABELS = {
  basic: 'Basic',
  standard: 'Standard',
  premium: 'Premium'
};

// User Levels
export const USER_LEVELS = {
  BRONZE: { name: 'Bronze', color: 'text-yellow-600', badge: '🥉' },
  SILVER: { name: 'Silver', color: 'text-gray-500', badge: '🥈' },
  GOLD: { name: 'Gold', color: 'text-yellow-500', badge: '🥇' },
  PLATINUM: { name: 'Platinum', color: 'text-purple-600', badge: '💎' }
};

// Notification Types
export const NOTIFICATION_TYPES = {
  ORDER_CREATED: 'order_created',
  ORDER_UPDATED: 'order_updated',
  PAYMENT_RECEIVED: 'payment_received',
  MESSAGE_RECEIVED: 'message_received',
  GIG_APPROVED: 'gig_approved',
  VERIFICATION_COMPLETE: 'verification_complete',
  DISPUTE_CREATED: 'dispute_created',
  RANKING_UPDATED: 'ranking_updated'
};

// Ghana Regions
export const GHANA_REGIONS = [
  'Greater Accra', 'Ashanti', 'Western', 'Eastern', 'Northern',
  'Volta', 'Central', 'Upper East', 'Upper West', 'Brong-Ahafo',
  'Western North', 'Ahafo', 'Bono', 'Bono East', 'Oti', 'Savannah',
  'North East'
];

// Default Categories
export const GIG_CATEGORIES = [
  {
    id: 1,
    name: 'Technology & Programming',
    icon: '💻',
    subcategories: [
      'Web Development', 'Mobile Apps', 'Software Development',
      'Database Management', 'DevOps', 'IT Support'
    ]
  },
  {
    id: 2,
    name: 'Design & Creative',
    icon: '🎨',
    subcategories: [
      'Logo Design', 'Web Design', 'Graphics Design',
      'Video Editing', 'Photography', 'Branding'
    ]
  },
  {
    id: 3,
    name: 'Writing & Translation',
    icon: '✍️',
    subcategories: [
      'Content Writing', 'Copywriting', 'Translation',
      'Proofreading', 'Technical Writing', 'Creative Writing'
    ]
  },
  {
    id: 4,
    name: 'Business & Finance',
    icon: '💼',
    subcategories: [
      'Business Planning', 'Financial Analysis', 'Accounting',
      'Market Research', 'Project Management', 'Consulting'
    ]
  },
  {
    id: 5,
    name: 'Marketing & Sales',
    icon: '📈',
    subcategories: [
      'Digital Marketing', 'Social Media', 'SEO',
      'Lead Generation', 'Email Marketing', 'Sales Strategy'
    ]
  },
  {
    id: 6,
    name: 'Education & Training',
    icon: '📚',
    subcategories: [
      'Online Tutoring', 'Course Creation', 'Training Programs',
      'Educational Content', 'Skills Assessment', 'Coaching'
    ]
  },
  {
    id: 7,
    name: 'Home & Lifestyle',
    icon: '🏠',
    subcategories: [
      'Home Cleaning', 'Cooking', 'Personal Shopping',
      'Pet Care', 'Gardening', 'Home Repairs'
    ]
  },
  {
    id: 8,
    name: 'Delivery & Logistics',
    icon: '🚚',
    subcategories: [
      'Package Delivery', 'Food Delivery', 'Moving Services',
      'Courier Services', 'Shopping Delivery', 'Document Services'
    ]
  },
  {
    id: 9,
    name: 'Events & Entertainment',
    icon: '🎉',
    subcategories: [
      'Event Planning', 'Photography', 'DJ Services',
      'Catering', 'Entertainment', 'Wedding Services'
    ]
  },
  {
    id: 10,
    name: 'Health & Wellness',
    icon: '🏥',
    subcategories: [
      'Fitness Training', 'Nutrition Consulting', 'Mental Health',
      'Healthcare Support', 'Wellness Coaching', 'Therapy'
    ]
  }
];

// Form Validation Rules
export const VALIDATION_RULES = {
  PASSWORD: {
    minLength: 8,
    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
    message: 'Password must be at least 8 characters with uppercase, lowercase, number, and special character'
  },
  PHONE: {
    pattern: /^\+233[0-9]{9}$|^0[0-9]{9}$/,
    message: 'Please enter a valid Ghana phone number'
  },
  EMAIL: {
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Please enter a valid email address'
  },
  USERNAME: {
    pattern: /^[a-zA-Z0-9_]{3,20}$/,
    message: 'Username must be 3-20 characters, letters, numbers, and underscores only'
  }
};

// File Upload Limits
export const UPLOAD_LIMITS = {
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  MAX_FILES: 10,
  ALLOWED_TYPES: ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx'],
  PROFILE_IMAGE_SIZE: 2 * 1024 * 1024 // 2MB
};

// Pagination Defaults
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 12,
  MAX_LIMIT: 50
};

// Socket Events
export const SOCKET_EVENTS = {
  CONNECTION: 'connection',
  DISCONNECT: 'disconnect',
  JOIN_ROOM: 'join_room',
  LEAVE_ROOM: 'leave_room',
  NEW_MESSAGE: 'new_message',
  MESSAGE_READ: 'message_read',
  ORDER_UPDATE: 'order_update',
  NOTIFICATION: 'notification',
  TYPING: 'typing',
  STOP_TYPING: 'stop_typing'
};

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'gigship_token',
  USER_DATA: 'gigship_user',
  THEME: 'gigship_theme',
  LANGUAGE: 'gigship_language',
  RECENT_SEARCHES: 'gigship_recent_searches',
  DRAFT_GIG: 'gigship_draft_gig',
  CART: 'gigship_cart'
};

// Toast Types
export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

// Modal Types
export const MODAL_TYPES = {
  CONFIRM: 'confirm',
  ALERT: 'alert',
  CUSTOM: 'custom'
};

// Theme Colors
export const THEME_COLORS = {
  PRIMARY: {
    50: '#f0f9ff',
    100: '#e0f2fe',
    500: '#0ea5e9',
    600: '#0284c7',
    700: '#0369a1'
  },
  SUCCESS: '#10b981',
  WARNING: '#f59e0b',
  ERROR: '#ef4444',
  INFO: '#3b82f6'
};

// Navigation Menu Items
export const NAVIGATION_ITEMS = {
  GIGGER: [
    { name: 'Dashboard', path: '/dashboard', icon: '📊' },
    { name: 'My Gigs', path: '/my-gigs', icon: '💼' },
    { name: 'Orders', path: '/orders', icon: '📋' },
    { name: 'Messages', path: '/messages', icon: '💬' },
    { name: 'Earnings', path: '/earnings', icon: '💰' },
    { name: 'Profile', path: '/profile', icon: '👤' }
  ],
  SHIPPER: [
    { name: 'Dashboard', path: '/dashboard', icon: '📊' },
    { name: 'Browse Gigs', path: '/browse', icon: '🔍' },
    { name: 'My Orders', path: '/orders', icon: '📋' },
    { name: 'Messages', path: '/messages', icon: '💬' },
    { name: 'Favorites', path: '/favorites', icon: '❤️' },
    { name: 'Profile', path: '/profile', icon: '👤' }
  ],
  HYBRID: [
    { name: 'Dashboard', path: '/dashboard', icon: '📊' },
    { name: 'Browse Gigs', path: '/browse', icon: '🔍' },
    { name: 'My Gigs', path: '/my-gigs', icon: '💼' },
    { name: 'Orders', path: '/orders', icon: '📋' },
    { name: 'Messages', path: '/messages', icon: '💬' },
    { name: 'Earnings', path: '/earnings', icon: '💰' },
    { name: 'Profile', path: '/profile', icon: '👤' }
  ]
};

// Dashboard Widgets
export const DASHBOARD_WIDGETS = {
  GIGGER: [
    'active_gigs', 'pending_orders', 'recent_earnings',
    'rating_overview', 'completion_rate', 'recent_reviews'
  ],
  SHIPPER: [
    'active_orders', 'saved_gigs', 'recent_purchases',
    'favorite_giggers', 'order_history', 'recommendations'
  ],
  HYBRID: [
    'overview_stats', 'active_gigs', 'active_orders',
    'earnings_summary', 'recent_activity', 'quick_actions'
  ]
};

// Currency Formatting
export const CURRENCY = {
  CODE: 'GHS',
  SYMBOL: 'GH₵',
  DECIMAL_PLACES: 2
};

// Date Formats
export const DATE_FORMATS = {
  FULL: 'MMMM DD, YYYY',
  SHORT: 'MMM DD, YYYY',
  TIME: 'HH:mm',
  DATETIME: 'MMM DD, YYYY HH:mm',
  ISO: 'YYYY-MM-DD'
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  UNAUTHORIZED: 'Your session has expired. Please login again.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  SERVER_ERROR: 'Something went wrong. Please try again later.',
  FILE_TOO_LARGE: 'File size exceeds the maximum limit.',
  INVALID_FILE_TYPE: 'Invalid file type. Please upload a supported file.',
  PAYMENT_ERROR: 'Payment failed. Please check your payment details.'
};

// Success Messages
export const SUCCESS_MESSAGES = {
  PROFILE_UPDATED: 'Profile updated successfully!',
  GIG_CREATED: 'Gig posted successfully!',
  ORDER_PLACED: 'Order placed successfully!',
  PAYMENT_SUCCESS: 'Payment completed successfully!',
  MESSAGE_SENT: 'Message sent successfully!',
  REVIEW_SUBMITTED: 'Review submitted successfully!',
  SETTINGS_SAVED: 'Settings saved successfully!',
  FILE_UPLOADED: 'File uploaded successfully!',
  VERIFICATION_SENT: 'Verification code sent to your phone!',
  ACCOUNT_VERIFIED: 'Account verified successfully!'
};

// Loading States
export const LOADING_STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error'
};

// Animation Durations (in milliseconds)
export const ANIMATIONS = {
  FAST: 150,
  NORMAL: 300,
  SLOW: 500,
  TOAST_DURATION: 4000,
  TOOLTIP_DELAY: 500
};

// Breakpoints (following Tailwind CSS)
export const BREAKPOINTS = {
  SM: '640px',
  MD: '768px',
  LG: '1024px',
  XL: '1280px',
  '2XL': '1536px'
};

// Feature Flags
export const FEATURES = {
  REAL_TIME_CHAT: true,
  PUSH_NOTIFICATIONS: true,
  DARK_MODE: true,
  MULTI_LANGUAGE: false,
  VIDEO_CALLS: false,
  AI_MATCHING: false,
  ADVANCED_ANALYTICS: true,
  SUBSCRIPTION_PLANS: true,
  BULK_ORDERS: true,
  DISPUTE_RESOLUTION: true
};

// Route Paths
export const ROUTES = {
  // Public Routes
  HOME: '/',
  BROWSE: '/browse',
  GIG_DETAILS: '/gig/:id',
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  GO: '/go',

  // Protected Routes
  DASHBOARD: '/dashboard',
  PROFILE: '/dashboard/profile',
  PROFILE_USER: '/profile/:userId',
  SETTINGS: '/dashboard/settings',
  MESSAGES: '/dashboard/messages',
  ORDERS: '/dashboard/orders',
  EARNINGS: '/dashboard/earnings',
  WALLET: '/dashboard/wallet',

  // Gigger Routes
  MY_GIGS: '/my-gigs',
  CREATE_GIG: '/create-gig',
  EDIT_GIG: '/edit-gig/:id',
  GIG_ANALYTICS: '/gig-analytics/:id',

  // Shipper Routes
  FAVORITES: '/favorites',
  ORDER_HISTORY: '/order-history',
  POST_REQUEST: '/post-request',
  HELP: '/help',
  ABOUT: '/about',
  CONTACT: '/contact'
};

// Quick Actions for Dashboard - UPDATED with correct route references
export const QUICK_ACTIONS = {
  GIGGER: [
    { label: 'Create New Gig', path: ROUTES.CREATE_GIG, icon: '➕', color: 'bg-blue-600' },
    { label: 'Manage Gigs', path: ROUTES.MY_GIGS, icon: '📝', color: 'bg-green-600' },
    { label: 'View Messages', path: ROUTES.MESSAGES, icon: '💬', color: 'bg-purple-600' },
    { label: 'Check Earnings', path: ROUTES.EARNINGS, icon: '💰', color: 'bg-yellow-600' }
  ],
  SHIPPER: [
    { label: 'Browse Gigs', path: ROUTES.BROWSE, icon: '🔍', color: 'bg-blue-600' },
    { label: 'Post Request', path: ROUTES.POST_REQUEST, icon: '📋', color: 'bg-green-600' },
    { label: 'View Orders', path: ROUTES.ORDERS, icon: '📦', color: 'bg-purple-600' },
    { label: 'Messages', path: ROUTES.MESSAGES, icon: '💬', color: 'bg-orange-600' }
  ],
  HYBRID: [
    { label: 'Create New Gig', path: ROUTES.CREATE_GIG, icon: '➕', color: 'bg-blue-600' },
    { label: 'Browse Gigs', path: ROUTES.BROWSE, icon: '🔍', color: 'bg-green-600' },
    { label: 'Post Request', path: ROUTES.POST_REQUEST, icon: '📋', color: 'bg-purple-600' },
    { label: 'View Orders', path: ROUTES.ORDERS, icon: '📦', color: 'bg-orange-600' },
    { label: 'Messages', path: ROUTES.MESSAGES, icon: '💬', color: 'bg-indigo-600' },
    { label: 'Check Earnings', path: ROUTES.EARNINGS, icon: '💰', color: 'bg-yellow-600' }
  ]
};

// Status Icons
export const STATUS_ICONS = {
  [ORDER_STATUS.PENDING]: '⏳',
  [ORDER_STATUS.ACCEPTED]: '✅',
  [ORDER_STATUS.IN_PROGRESS]: '🔄',
  [ORDER_STATUS.DELIVERED]: '📦',
  [ORDER_STATUS.COMPLETED]: '✨',
  [ORDER_STATUS.CANCELLED]: '❌',
  [ORDER_STATUS.DISPUTED]: '⚠️',
  [ORDER_STATUS.REFUNDED]: '💸'
};

// Priority Levels
export const PRIORITY_LEVELS = {
  LOW: { label: 'Low', color: 'text-green-600', badge: 'bg-green-100' },
  MEDIUM: { label: 'Medium', color: 'text-yellow-600', badge: 'bg-yellow-100' },
  HIGH: { label: 'High', color: 'text-orange-600', badge: 'bg-orange-100' },
  URGENT: { label: 'Urgent', color: 'text-red-600', badge: 'bg-red-100' }
};

// Search Filters
export const SEARCH_FILTERS = {
  PRICE_RANGES: [
    { label: 'Under GH₵25', min: 0, max: 25 },
    { label: 'GH₵25 - GH₵50', min: 25, max: 50 },
    { label: 'GH₵50 - GH₵100', min: 50, max: 100 },
    { label: 'GH₵100 - GH₵250', min: 100, max: 250 },
    { label: 'GH₵250 - GH₵500', min: 250, max: 500 },
    { label: 'Over GH₵500', min: 500, max: null }
  ],
  DELIVERY_TIMES: [
    { label: '24 hours', value: 1 },
    { label: '3 days', value: 3 },
    { label: '7 days', value: 7 },
    { label: '2 weeks', value: 14 },
    { label: '1 month', value: 30 }
  ],
  RATING_FILTERS: [
    { label: '5 stars only', value: 5 },
    { label: '4+ stars', value: 4 },
    { label: '3+ stars', value: 3 },
    { label: 'Any rating', value: 0 }
  ],
  SORT_OPTIONS: [
    { label: 'Relevance', value: 'relevance' },
    { label: 'Price: Low to High', value: 'price_asc' },
    { label: 'Price: High to Low', value: 'price_desc' },
    { label: 'Newest First', value: 'newest' },
    { label: 'Best Rated', value: 'rating' },
    { label: 'Most Orders', value: 'orders' }
  ]
};

// Subscription Plans
export const SUBSCRIPTION_PLANS = {
  FREE: {
    name: 'Free',
    price: 0,
    period: 'forever',
    features: [
      'Basic gig posting',
      'Standard commission rates',
      'Basic customer support',
      'Limited analytics'
    ],
    limits: {
      activeGigs: 3,
      monthlyOrders: 10,
      portfolioImages: 5
    }
  },
  PRO: {
    name: 'Pro',
    price: 29,
    period: 'month',
    features: [
      'Unlimited gig posting',
      'Reduced commission rates',
      'Priority customer support',
      'Advanced analytics',
      'Featured listings',
      'Bulk order access'
    ],
    limits: {
      activeGigs: 'unlimited',
      monthlyOrders: 'unlimited',
      portfolioImages: 20
    },
    savings: '2% commission reduction'
  },
  ENTERPRISE: {
    name: 'Enterprise',
    price: 99,
    period: 'month',
    features: [
      'Everything in Pro',
      'Maximum commission reduction',
      'Dedicated account manager',
      'Custom integrations',
      'Premium featured listings',
      'Advanced dispute resolution'
    ],
    limits: {
      activeGigs: 'unlimited',
      monthlyOrders: 'unlimited',
      portfolioImages: 50
    },
    savings: '5% commission reduction'
  }
};

// Common Regular Expressions
export const REGEX = {
  PHONE_GHANA: /^\+233[0-9]{9}$|^0[0-9]{9}$/,
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PASSWORD_STRONG: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
  USERNAME: /^[a-zA-Z0-9_]{3,20}$/,
  SLUG: /^[a-z0-9-]+$/,
  URL: /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i,
  AMOUNT: /^\d+(\.\d{1,2})?$/
};

// Default Values
export const DEFAULTS = {
  PROFILE_IMAGE: '/images/default-avatar.png',
  GIG_IMAGE: '/images/default-gig.png',
  ITEMS_PER_PAGE: 12,
  DEBOUNCE_DELAY: 500,
  AUTO_SAVE_DELAY: 2000,
  SESSION_TIMEOUT: 24 * 60 * 60 * 1000, // 24 hours
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000
};

// Component Sizes
export const SIZES = {
  AVATAR: {
    XS: 'w-6 h-6',
    SM: 'w-8 h-8',
    MD: 'w-10 h-10',
    LG: 'w-12 h-12',
    XL: 'w-16 h-16',
    '2XL': 'w-20 h-20'
  },
  BUTTON: {
    XS: 'px-2 py-1 text-xs',
    SM: 'px-3 py-1.5 text-sm',
    MD: 'px-4 py-2 text-sm',
    LG: 'px-6 py-3 text-base',
    XL: 'px-8 py-4 text-lg'
  },
  INPUT: {
    SM: 'px-3 py-1.5 text-sm',
    MD: 'px-4 py-2 text-sm',
    LG: 'px-4 py-3 text-base'
  }
};

// Export default object for easy importing
const constants = {
  API_BASE_URL,
  SOCKET_URL,
  USER_ROLES,
  ORDER_STATUS,
  ORDER_STATUS_LABELS,
  ORDER_STATUS_COLORS,
  PAYMENT_STATUS,
  GIG_STATUS,
  LOCATION_TYPES,
  LOCATION_TYPE_LABELS,
  PACKAGE_TYPES,
  PACKAGE_TYPE_LABELS,
  USER_LEVELS,
  NOTIFICATION_TYPES,
  GHANA_REGIONS,
  GIG_CATEGORIES,
  VALIDATION_RULES,
  UPLOAD_LIMITS,
  PAGINATION,
  SOCKET_EVENTS,
  STORAGE_KEYS,
  TOAST_TYPES,
  MODAL_TYPES,
  THEME_COLORS,
  NAVIGATION_ITEMS,
  DASHBOARD_WIDGETS,
  CURRENCY,
  DATE_FORMATS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  LOADING_STATES,
  ANIMATIONS,
  BREAKPOINTS,
  FEATURES,
  ROUTES,
  QUICK_ACTIONS,
  STATUS_ICONS,
  PRIORITY_LEVELS,
  SEARCH_FILTERS,
  SUBSCRIPTION_PLANS,
  REGEX,
  DEFAULTS,
  SIZES
};

export default constants;